/**
 * Helper functions for transforming palettes to and from convenience types.
 */
import { alphaBlend } from '@napolab/alpha-blend';
import chroma from 'chroma-js';
import tinycolor from 'tinycolor2';

import { randomChoice } from 'base/random';
import {
  ChromaticShades,
  ColorVariable,
  CompiledChromaticColor,
  CompiledConcept,
  CompiledNeutralColor,
  NeutralShades,
  NEUTRAL_DARKEST,
} from 'base/styleguide/types';
import Color from 'colorjs.io';
import clamp from 'lodash/clamp';
import randomColor from 'randomcolor';
import { NEUTRAL_TINT_OPACITY } from './concept';

export interface WeightedItem {
  weight: number;
}

/**
 * The amount to blend the primary color into the generated palette to harmonize, where 0 would be none of the primary blended and 0 would be entirely primary.
 */
const HARMONY_BLEND = 15;

export function randomHexColorCode() {
  return randomChoice(...generateRandomRainbow());
}

export function generateRandomRainbow(count = 1) {
  const constrainColor = hsvRadialConstraintFactory({
    distance: 0.85,
    value: 1,
    saturation: 1,
  });

  return [
    ...randomColor({ luminosity: 'random', count, hue: 'red' }),
    ...randomColor({ luminosity: 'random', count, hue: 'orange' }),
    ...randomColor({ luminosity: 'random', count, hue: 'yellow' }),
    ...randomColor({ luminosity: 'random', count, hue: 'green' }),
    ...randomColor({ luminosity: 'random', count, hue: 'blue' }),
    ...randomColor({ luminosity: 'random', count, hue: 'purple' }),
    ...randomColor({ luminosity: 'random', count, hue: 'pink' }),
  ].map(constrainColor);
}

function randomizeSaturation(source: tinycolor.ColorFormats.HSVA) {
  const saturation = Math.random() * 100;
  return tinycolor({ ...source, s: saturation }).toHexString();
}

/**
 * Blends in black or white with a given color at a given opacity.
 */
function blend(color: string, overlay: 'black' | 'white', opacity: number) {
  const rgb = overlay === 'white' ? '255,255,255' : '0,0,0';
  return alphaBlend(color, `rgba(${rgb}, ${opacity})`)
    .slice(0, -2)
    .toUpperCase();
}

/**
 * Given a source angle in HSV, return a new angle with some fuzziness.
 */
function fuzzNumber(number: number, maxFuzz = 30) {
  const delta = (2 * Math.random() - 1) * maxFuzz;
  return number + delta;
}

function fuzz(color: tinycolor.Instance, heat = 0.7) {
  const { h, s, v } = color.toHsv();
  const angleFuzz = 30 + 20 * heat;
  const saturationFuzz = 0.3 * Math.max(0, heat - 0.4);
  const valueFuzz = 0.2 * Math.max(0, heat - 0.6);

  return tinycolor({
    h: fuzzNumber(h, angleFuzz),
    s: fuzzNumber(s, saturationFuzz),
    v: fuzzNumber(v, valueFuzz),
  });
}

/**
 * Evenly spaces colors around the color wheel to generate new chromatics. We manually
 * replace the third color with the direct complement for better harmony, and also apply
 * a tint of the original color to each chromatic.
 */
function generatePentanticPalette(
  concept: CompiledConcept,
  source: tinycolor.Instance,
) {
  const degrees = 60;
  const complement = source.complement().toHexString();
  const newChromatics = [];
  for (let i = 0; i < 4; i++) {
    const rotation = fuzzNumber(degrees);
    const newColor = source.spin(rotation);
    const blendedColor = tinycolor.mix(
      newColor,
      concept.chromatics[0].hex,
      HARMONY_BLEND,
    );
    newChromatics.push(blendedColor.toHexString());
  }
  // Manually replace the third color with the direct complement for better harmony.
  newChromatics[2] = complement;
  return newChromatics;
}

/**
 * Generates a nice gradient from primary to complementary through LCH color space.
 */
function generateLCHGradient(source: tinycolor.Instance) {
  const complement = source.complement().toHexString();
  const lchScale = chroma.scale([source.toHexString(), complement]).mode('lch');
  const newChromatics: string[] = [];
  [0.25, 0.5, 0.75]
    .map((step) => lchScale(step).hex())
    .map((c) => {
      newChromatics.push(c);
    });
  return newChromatics;
}

export function generateNeutralShades(colorHex: string): NeutralShades {
  return [
    blend(colorHex, 'white', 1),
    blend(colorHex, 'white', 0.95),
    blend(colorHex, 'white', 0.85),
    blend(colorHex, 'white', 0.7),
    blend(colorHex, 'white', 0.5),
    blend(colorHex, 'white', 0.3),
    blend(colorHex, 'white', 0.1),
    blend(colorHex, 'white', 0),
  ];
}

/** Given a source color, generate 3 lighter and 3 darker shades. */
export function generateShades(colorHex: string): ChromaticShades {
  return [
    blend(colorHex, 'white', 0.9),
    blend(colorHex, 'white', 0.8),
    blend(colorHex, 'white', 0.3),
    colorHex,
    blend(colorHex, 'black', 0.2),
    blend(colorHex, 'black', 0.6),
    blend(colorHex, 'black', 0.7),
  ];
}

/** Pick an item from an array psudo-randomly. */
export function pick<T>(arr: T[]) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Pick a item from an array psudo-randomly with a bias towards items with
 * higher weights. `arr` must have a `weight` field which contains a number from
 * 0 to 1. All weights of all values in array must add up to `1`.
 */
export function weightedPick<T extends WeightedItem>(arr: T[]) {
  const rand = Math.random();
  let sum = 0;

  // This effectively creates a line where each item takes up area propotional
  //  to it's weight i.e.
  //         arr[0]           arr[1]      arr[2]
  // |--------0.6--------||----0.3----||----0.3----|
  // We then pick a random number between 0 and 1 and figure out which zone it
  // falls into by summing weight values until we exceed the random number.
  // |--------0.6--------||----0.3----||----0.3----|
  //                               ^
  //                              0.8
  // 0.6 (0.6<0.8) 0.6+0.3=0.9 (0.9 > 0.8), return scheme arr[1];
  for (const item of arr) {
    sum += item.weight;
    if (sum > rand) {
      return item;
    }
  }
  // We are guarenteed to return a item as the sum of all chances is 1 and
  // math.random will return a number less then 1. Only way we can get here
  // is if the input array was empty or the weights didn't add up to 1.
  throw new Error('Input array to weightedPick was malformed');
}

export function extractPaletteColor(
  concept: {
    colorVariables: ReadonlyMap<ColorVariable, string>;
    neutral: CompiledNeutralColor;
    chromatics: ReadonlyArray<CompiledChromaticColor>;
  },
  color: ColorVariable,
) {
  const match = color.match(/^(neutral-(?:white|shade-7))(?:-(\d+))?/);
  if (match != null) {
    const [_, shadeRaw, opacityRaw] = match;
    const colorVariable =
      shadeRaw === 'neutral-white' ? 'neutral-white' : NEUTRAL_DARKEST;
    const hex = concept.colorVariables.get(colorVariable);

    const instance = tinycolor(hex);
    return opacityRaw == null
      ? instance.toHexString()
      : instance.setAlpha(parseInt(opacityRaw, 10) / 100).toHex8String();
  }

  const paletteName = color.split('-')[0];
  const shade = parseInt(color.split('-')[2]);

  if (paletteName === 'neutral') {
    return concept.neutral.shades[shade];
  }
  // The algo may reference a color we don't have, in that case just return
  // a color we do have.
  let chromaticIndex = parseInt(/chromatic(\d)/.exec(paletteName)![1]) - 1;
  let chromatic = concept.chromatics[chromaticIndex];

  while (chromaticIndex >= 0 && chromatic === undefined) {
    chromatic = concept.chromatics[chromaticIndex--];
  }
  return chromatic.shades[shade - 1];
}

function randomHarmonizedColor(primary: string) {
  const color = randomHexColorCode();
  const opacityInHex = (0.2 * 255).toString(16);
  return alphaBlend(color, `${primary}${opacityInHex}`)
    .slice(0, -2)
    .toUpperCase();
}

const ChromaDistanceMin = 15;
const PrimarySVDistanceMax = 0.05;

/**
 * Suggests a color for a given concept and chromatic index. The color will be
 * harmonious with the primary color while different from existing chromatics.
 */
export function suggestColor(concept: CompiledConcept, chromaticIndex: number) {
  const primaryHex = concept.chromatics[0].hex;
  const primaryColor = tinycolor(primaryHex);

  // Any colors suggested should be constrained to be within a certain range of
  // the primary color. This gives the palette a consistent vibe.
  const { v: vPrimary, s: sPrimary } = primaryColor.toHsv();
  const constrainColor = hsvRadialConstraintFactory({
    distance: PrimarySVDistanceMax,
    saturation: sPrimary,
    value: vPrimary,
  });

  // If we're suggesting the second color, we want to suggest a color that
  // somewhat fits with the primary.
  if (chromaticIndex === 1) {
    const complementary = primaryColor.complement();
    const gradientColors = generateLCHGradient(tinycolor(primaryHex)).map((c) =>
      tinycolor(c),
    );

    // These are not true random, they trend towards either known nice colors
    // or colors that are harmonious with the primary.
    const options = [
      fuzz(complementary).toHexString(),
      ...gradientColors.map((color) => fuzz(color).toHexString()),
      randomHarmonizedColor(primaryHex),
      randomHexColorCode(),
    ]
      .map(constrainColor)
      .filter((c) => Color.deltaE2000(primaryHex, c) > ChromaDistanceMin);

    // If we can't find a color that's different enough, just return a random one.
    if (options.length === 0) {
      return constrainColor(randomHexColorCode());
    }

    return randomChoice(...options);
  }

  // Compute the distance between the colors to inform the suggestion.
  const distances: number[] = [];
  const colors = concept.chromatics;
  for (let i = 1; i < colors.length; i++) {
    const prevColor = colors[i - 1].hex;
    const chromatic = colors[i].hex;
    distances.push(Color.deltaE2000(prevColor, chromatic));
  }
  const avgDistance =
    distances.reduce((acc, d) => acc + d, 0) / distances.length;
  let options: string[];
  if (avgDistance < 150) {
    // Return a color that's harmonious with the primary.
    options = generateLCHGradient(tinycolor(primaryHex));
  } else {
    // Return a more complementary color.
    options = generatePentanticPalette(concept, tinycolor(primaryHex));
  }
  // Remove any colors that are too similar to the existing chromatics.
  options = options
    .map(constrainColor)
    .filter((c) =>
      colors.every(
        (color) => Color.deltaE2000(color.hex, c) > ChromaDistanceMin,
      ),
    );

  // Fall back to random colors if we can't find a good one.
  if (options.length === 0) {
    options = generateRandomRainbow(50)
      .map(constrainColor)
      .filter((c) =>
        colors.every(
          (color) => Color.deltaE2000(color.hex, c) > ChromaDistanceMin,
        ),
      );
  }

  // It's technically possible we still don't find a color that's different
  // enough from the existing chromatics, in this case give up.
  return randomChoice(...options) ?? constrainColor(randomHexColorCode());
}

/**
 * Generates a function that constrains the saturation and value of a color to
 * be within a certain distance of a target color.
 */
export const hsvRadialConstraintFactory =
  (target: { distance: number; saturation: number; value: number }) =>
  (hex: string) => {
    let { h: hue, s: saturation, v: value } = tinycolor(hex).toHsv();

    // Constrain input to produce sane results
    const maxDistance = clamp(target.distance, 0, 1);
    const targetSaturation = clamp(target.saturation, 0, 1);
    const targetValue = clamp(target.value, 0, 1);

    // Constrain values to limits
    saturation = clamp(saturation, 0, 1);
    value = clamp(value, 0, 1);

    // Calculate distance from target
    const deltaSaturation = saturation - targetSaturation;
    const deltaValue = value - targetValue;
    const distance = Math.sqrt(
      Math.pow(deltaSaturation, 2) + Math.pow(deltaValue, 2),
    );

    // If the distance is within the target, return the input
    if (distance <= maxDistance) {
      return hex;
    }

    // Calculate the angle from the target
    const radians = Math.atan2(deltaValue, deltaSaturation);
    const distanceModulo = distance % maxDistance;

    // Calculate the new saturation and value
    const newSaturation = targetSaturation + Math.cos(radians) * distanceModulo;
    const newValue = targetValue + Math.sin(radians) * distanceModulo;

    return tinycolor({
      h: hue,
      s: clamp(newSaturation, 0, 1),
      v: clamp(newValue, 0, 1),
    }).toHexString();
  };

export function getNeutralHex(
  tintHex?: string,
  opacity = NEUTRAL_TINT_OPACITY,
): string {
  return tintHex == null
    ? '#000000'
    : tinycolor
        .mix('black', tintHex, opacity * 100)
        .toHexString()
        .toUpperCase();
}
